import * as Auth from '../../../authentication/state/Auth.selectors';
import * as Cookies from './cookies/Cookies.selectors';
import * as Disclosures from './disclosures/Disclosures.selectors';
import * as IAB from './iab/IAB.selectors';
import * as Palette from './palette/Palette.selectors';
import * as Scripts from './scripts/Scripts.selectors';

import { DOMAIN } from './Configuration.constants';
import { defaultConfiguration } from '../../constants';
import get from 'lodash/get';
import { getTcfV2Vendors } from '../../../common/state/db/iab/Iab.selectors';

export const cookies = Cookies;
export const disclosures = Disclosures;
export const iab = IAB;
export const palette = Palette;
export const scripts = Scripts;

export const getStore = state => get(state, DOMAIN) || {};
export const getField = (state, field, defaultValue = '') => {
    const value = getStore(state)[field];
    return value === undefined || value === null ? defaultValue : value;
};

export const getPayload = state => {
    const isCodeSplittingAllowedByPlan = Auth.customer.getCodeSplittingAllowedByPlan(state);
    const hasPermissionToUpdateCrossDomain = Auth.user.hasPermission(state, {
        toPerform: 'write',
        on: 'consent_cross_domain',
    });
    const hasPermissionToSetCCPARelaxed = Auth.user.hasPermission(state, {
        toPerform: 'write',
        on: 'ccpa_opt_out',
    });
    const hasPermissionToSetIABEnabled = Auth.user.hasPermission(state, {
        toPerform: 'write',
        on: 'iab_eu_tcf_2',
    });
    const hasPermissionToSetManagePreferences = Auth.user.hasPermission(state, {
        toPerform: 'write',
        on: 'consent_manage_prefs',
    });
    const codeSplitting = isCodeSplittingAllowedByPlan ? getField(state, 'codeSplitting') : false;
    const crossDomain = hasPermissionToUpdateCrossDomain ? getField(state, 'crossDomain') : false;
    const ccpaRelaxed = hasPermissionToSetCCPARelaxed ? !!getField(state, 'ccpaRelaxed') : false;
    const managePreferencesEnabled = hasPermissionToSetManagePreferences
        ? !!getField(state, 'managePreferencesEnabled')
        : false;

    const iabData = IAB.getStore(state);

    // Process IAB vendors
    const tcfv2Vendors = getTcfV2Vendors(state, iabData?.tcf?.vendors);

    // Add remoteConsent to state if it's true
    // Unset duration is dropped so the CMP applies its default
    const remoteConsent = getField(state, 'remoteConsent');
    const remoteConsentObj = remoteConsent
        ? {
              remoteConsent,
              crossDeviceNoticeSeconds: getField(state, 'crossDeviceNoticeSeconds') || undefined,
              showCrossDeviceNotice: getField(state, 'showCrossDeviceNotice', null),
          }
        : {};

    return {
        additionalLinks: getField(state, 'additionalLinks', defaultConfiguration.additionalLinks),
        allowTimeout: getField(state, 'allowTimeout', defaultConfiguration.allowTimeout),
        analyticsOnByDefault: getField(
            state,
            'analyticsOnByDefault',
            defaultConfiguration.analyticsOnByDefault
        ),
        analyticsOnByDefaultJurisdictions: getField(
            state,
            'analyticsOnByDefaultJurisdictions',
            defaultConfiguration.analyticsOnByDefaultJurisdictions
        ),
        ccpaRelaxed,
        codeSplitting,
        cookies: cookies.getStore(state),
        crossDomain,
        deleteStorageOnOptout: getField(state, 'deleteStorageOnOptout') || false,
        disclosures: disclosures.getResults(state),
        dntSupport: getField(state, 'dntSupport') || false,
        documentPatching: getField(
            state,
            'documentPatching',
            defaultConfiguration.documentPatching
        ),
        enableDoNotSell: getField(state, 'enableDoNotSell', defaultConfiguration.enableDoNotSell),
        enableDoNotSellDefault: getField(
            state,
            'enableDoNotSellDefault',
            defaultConfiguration.enableDoNotSellDefault
        ),
        doNotSellCategories: getField(
            state,
            'doNotSellCategories',
            defaultConfiguration.doNotSellCategories
        ),
        externalStylesheet: getField(state, 'externalStylesheet') || false,
        forcedClassifyEnabled: getField(state, 'forcedClassifyEnabled') || false,
        forceManagePreferences: getField(state, 'forceManagePreferences') || false,
        googleConsent: getField(state, 'googleConsent') || false,
        amazonConsent: getField(state, 'amazonConsent') || false,
        gpcSupport: getField(state, 'gpcSupport') || false,
        iab: {
            ...iabData,
            gpp: {
                ...iabData?.gpp,
                enabled: hasPermissionToSetIABEnabled && !!iabData?.gpp?.enabled,
            },
            tcf: {
                ...iabData?.tcf,
                enabled: hasPermissionToSetIABEnabled && !!iabData?.tcf?.enabled,
                v2: {
                    vendors: tcfv2Vendors,
                },
            },
        },
        iframes: getField(state, 'iframes', defaultConfiguration.iframes),
        iframeBlocking: getField(state, 'iframeBlocking', defaultConfiguration.iframeBlocking),
        legacyBrowserSupport: getField(state, 'legacyBrowserSupport') || false,
        localStorage: getField(state, 'localStorage', defaultConfiguration.localStorage),
        localStorageBlocking: getField(
            state,
            'localStorageBlocking',
            defaultConfiguration.localStorageBlocking
        ),
        managePreferencesEnabled,
        microsoftConsent: getField(state, 'microsoftConsent') || false,
        palette: palette.getStore(state),
        policyLinkInDrawer: getField(state, 'policyLinkInDrawer'),
        policyLinkText: getField(state, 'policyLinkText', defaultConfiguration.policyLinkText),
        ...remoteConsentObj,
        scripts: scripts.getStore(state),
        showConsentUuid: getField(state, 'showConsentUuid', defaultConfiguration.showConsentUuid),
        showOptOutWidget: getField(
            state,
            'showOptOutWidget',
            defaultConfiguration.showOptOutWidget
        ),
        showWidget: getField(state, 'showWidget', defaultConfiguration.showWidget),
        storagePolicyHref: getField(state, 'storagePolicyHref'),
        tattleSampling: getField(state, 'tattleSampling', defaultConfiguration.tattleSampling),
        timeoutSeconds: parseInt(getField(state, 'timeoutSeconds') || 10, 10),
        translations: getField(state, 'translations'),
        variantMapping: getField(state, 'variantMapping', defaultConfiguration.variantMapping),
    };
};
